import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Croppie from "croppie";
import "croppie/croppie.css";
import "./Settings.css";
import { getAuthHeader } from "../utils/auth";

const PROD_API_URL = "https://theqahubweb-production.up.railway.app";
const ENV_API_URL = (process.env.REACT_APP_API_URL || "").trim().replace(/\/$/, "");
const CURRENT_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const NORMALIZED_ORIGIN = CURRENT_ORIGIN.includes("://www.theqahub.es")
  ? CURRENT_ORIGIN.replace("://www.", "://")
  : CURRENT_ORIGIN;
const getStoredApiUrl = () => {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage.getItem("theqahub_api_base") || "";
  } catch {
    return "";
  }
};
const STORED_API_URL = getStoredApiUrl();
const SHOULD_USE_ORIGIN = CURRENT_ORIGIN.includes("theqahub.es");
const API_URL = STORED_API_URL || (SHOULD_USE_ORIGIN ? NORMALIZED_ORIGIN : (ENV_API_URL || PROD_API_URL));

function Settings() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingAvatar, setSavingAvatar] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [email, setEmail] = useState("");
  const [nombre, setNombre] = useState("");
  const [apellidos, setApellidos] = useState("");

  // AVATAR
  const [avatarUrl, setAvatarUrl] = useState("");      // lo que viene del backend (avatarUrl)
  const [avatarError, setAvatarError] = useState(false);
  const [rawImageUrl, setRawImageUrl] = useState("");  // objectURL del archivo elegido
  const [showCrop, setShowCrop] = useState(false);

  const croppieElRef = useRef(null);
  const croppieInstanceRef = useRef(null);

  // 🔐 Cargar usuario
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          credentials: "include",
          headers: {
            ...getAuthHeader(),
          },
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data.user) {
          navigate("/login");
          return;
        }

        setEmail(data.user.email);
        setNombre(data.user.nombre || "");
        setApellidos(data.user.apellidos || "");
        setAvatarUrl(data.user.avatarUrl || "");
      } catch {
        setError("No se pudo cargar el perfil");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [navigate]);

  // ✅ Inicializar Croppie cuando hay imagen y el contenedor está renderizado
  useEffect(() => {
    if (!showCrop || !rawImageUrl || !croppieElRef.current) return;

    // destruir anterior
    if (croppieInstanceRef.current) {
      croppieInstanceRef.current.destroy();
      croppieInstanceRef.current = null;
    }

    croppieInstanceRef.current = new Croppie(croppieElRef.current, {
      viewport: { width: 180, height: 180, type: "circle" },
      boundary: { width: 340, height: 260 },
      showZoomer: true,
      enableOrientation: true,
      enableExif: true,
    });

    croppieInstanceRef.current.bind({ url: rawImageUrl });

    return () => {
      if (croppieInstanceRef.current) {
        croppieInstanceRef.current.destroy();
        croppieInstanceRef.current = null;
      }
    };
  }, [showCrop, rawImageUrl]);

  const onPickAvatar = (e) => {
    setError("");
    setSuccess("");
    setAvatarError(false);

    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Selecciona un archivo de imagen válido.");
      return;
    }

    // liberar objectURL anterior si existía
    if (rawImageUrl) URL.revokeObjectURL(rawImageUrl);

    const url = URL.createObjectURL(file);
    setRawImageUrl(url);
    setShowCrop(true);
  };

  const cancelCrop = () => {
    setShowCrop(false);
    if (rawImageUrl) URL.revokeObjectURL(rawImageUrl);
    setRawImageUrl("");
  };

  const getAvatarSrc = (url) => {
    if (!url) return "";
    if (/^(https?:)?\/\//i.test(url) || url.startsWith("data:") || url.startsWith("blob:")) {
      return url;
    }
    return `${API_URL}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  const onSaveAvatar = async () => {
    if (!croppieInstanceRef.current) {
      setError("Primero selecciona una imagen.");
      return;
    }

    setError("");
    setSuccess("");
    setSavingAvatar(true);

    try {
      // Genera el recorte como Blob (ya recortado en frontend)
      const blob = await croppieInstanceRef.current.result({
        type: "blob",
        size: { width: 256, height: 256 },
        format: "png",
        quality: 1,
        circle: false, // el viewport es circular visualmente; guardamos cuadrado y lo mostramos redondo por CSS
      });

      const fd = new FormData();
      fd.append("avatar", blob, "avatar.png");

      const res = await fetch(`${API_URL}/api/auth/avatar`, {
        method: "PUT",
        credentials: "include",
        headers: {
          ...getAuthHeader(),
        },
        body: fd,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.message || "No se pudo actualizar la foto");
        return;
      }

      // El backend debería devolver user.avatarUrl
      setAvatarUrl(data.user?.avatarUrl || "");
      setAvatarError(false);
      setSuccess("Foto de perfil actualizada ✅");

      cancelCrop();
    } catch (e) {
      setError("Error subiendo la foto (red/servidor)");
    } finally {
      setSavingAvatar(false);
    }
  };

  const onSaveProfile = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSaving(true);

    try {
      const res = await fetch(`${API_URL}/api/auth/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader(),
        },
        credentials: "include",
        body: JSON.stringify({ nombre, apellidos }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.message || "Error al guardar cambios");
        return;
      }

      setSuccess("Perfil actualizado correctamente ✅");
    } catch {
      setError("Error de red");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="settings-page">Cargando ajustes...</div>;
  }

  return (
    <div className="settings-page">
      <div className="settings-card">
        <h1>Ajustes de la cuenta</h1>
        <p className="muted">Gestiona tu información personal</p>

        {/* ===== AVATAR ===== */}
        <div className="settings-section">
          <h2 className="settings-subtitle">Foto de perfil</h2>

          <div className="avatar-row">
            <div className="avatar-preview">
              {avatarUrl && !avatarError ? (
                <img
                  src={getAvatarSrc(avatarUrl)}
                  alt="Avatar"
                  onError={() => setAvatarError(true)}
                />
              ) : (
                <div className="avatar-placeholder">👤</div>
              )}
            </div>

            <div className="avatar-actions">
              <label className="dash-btn" style={{ display: "inline-block", width: "fit-content" }}>
                Cambiar foto
                <input type="file" accept="image/*" onChange={onPickAvatar} style={{ display: "none" }} />
              </label>

              <div className="muted small">
                Recomendado: imagen cuadrada o con buena cara centrada.
              </div>
            </div>
          </div>

          {/* Croppie UI */}
          {showCrop && (
            <div className="crop-box">
              <div className="cropper" ref={croppieElRef} />

              <div className="crop-actions">
                <button type="button" className="dash-btn ghost" onClick={cancelCrop} disabled={savingAvatar}>
                  Cancelar
                </button>
                <button type="button" className="dash-btn" onClick={onSaveAvatar} disabled={savingAvatar}>
                  {savingAvatar ? "Guardando..." : "Guardar foto"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===== PERFIL ===== */}
        <div className="settings-section">
          <h2 className="settings-subtitle">Datos personales</h2>

          <form onSubmit={onSaveProfile} className="settings-form">
            <div className="field">
              <label>Email</label>
              <input type="email" value={email} disabled />
            </div>

            <div className="field">
              <label>Nombre</label>
              <input
                type="text"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                required
              />
            </div>

            <div className="field">
              <label>Apellidos</label>
              <input
                type="text"
                value={apellidos}
                onChange={(e) => setApellidos(e.target.value)}
                required
              />
            </div>

            {error && <p className="error">{error}</p>}
            {success && <p className="success">{success}</p>}

            <button type="submit" disabled={saving} className="dash-btn">
              {saving ? "Guardando..." : "Guardar cambios"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Settings;
